/**
 * Адрес машины словами вместо координат.
 *
 * «Friedrichstraße 43, Berlin» вместо «52.5077, 13.3971» — это то, что можно
 * продиктовать по телефону и вписать в документ.
 *
 * Спрашивается только при смене места. Координаты в зависимости округляются до
 * четырёх знаков — около одиннадцати метров: машина на стоянке дрожит в
 * пределах точности GPS, и без округления один и тот же адрес запрашивался бы
 * заново при каждом обновлении позиции. Сервер тоже помнит ответы, так что
 * это вторая линия защиты бесплатного порога, а не единственная.
 */

import { useEffect, useState } from 'react'

import { api } from './api'
import { dict, type Lang } from './i18n'

type Props = {
  latitude: number | undefined
  longitude: number | undefined
  lang: Lang
  /** Ключ Google не заведён — адресов нет, и место под них не занимаем. */
  enabled: boolean
}

export function Address({ latitude, longitude, lang, enabled }: Props) {
  const t = dict(lang)
  const [address, setAddress] = useState<string | null>(null)

  // Округление живёт в зависимости, а не в теле: иначе effect срабатывал бы
  // на каждое обновление позиции, даже когда машина стоит.
  const spot =
    typeof latitude === 'number' && typeof longitude === 'number'
      ? `${latitude.toFixed(4)},${longitude.toFixed(4)}`
      : null

  useEffect(() => {
    if (!enabled || !spot) {
      setAddress(null)
      return
    }
    let cancelled = false
    const [lat, lon] = spot.split(',').map(Number) as [number, number]
    api
      .addresses([{ latitude: lat, longitude: lon }])
      .then((found) => {
        if (!cancelled) setAddress(found[0] ?? null)
      })
      .catch(() => {
        // Адрес — украшение поверх координат. Не пришёл — экран остаётся живым.
        if (!cancelled) setAddress(null)
      })
    return () => {
      cancelled = true
    }
  }, [spot, enabled])

  if (!enabled || !spot) return null
  return <p className="c-address">{address ?? t.addressUnknown}</p>
}
